import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PlantCard } from '../components/PlantCard';
import { ImageUpload } from '../components/ImageUpload';
import { OcrImport } from '../components/OcrImport';
import { CsvImport } from '../components/CsvImport';
import { api } from '../api';
import type { Plant, PlantInput } from '../types';
import { CATEGORIES } from '../constants';

const EMPTY_FORM: PlantInput = {
  german_name: '',
  botanical_name: '',
  image_url: null,
  notes: '',
  category: CATEGORIES[0],
};

export const AdminPage: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Alle');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PlantInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showOcr, setShowOcr] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.getPlants();
      setPlants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Count per category
  const counts: Record<string, number> = { Alle: plants.length };
  for (const cat of CATEGORIES) {
    counts[cat] = plants.filter((p) => p.category === cat).length;
  }

  const filtered = plants.filter((p) => {
    const q = search.toLowerCase();
    const matchesSearch = (
      p.german_name.toLowerCase().includes(q) ||
      p.botanical_name.toLowerCase().includes(q)
    );
    const matchesCat = activeCategory === 'Alle' || p.category === activeCategory;
    return matchesSearch && matchesCat;
  });

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setShowForm(true);
  };

  const openEdit = (plant: Plant) => {
    setEditingId(plant.id);
    setForm({
      german_name: plant.german_name,
      botanical_name: plant.botanical_name,
      image_url: plant.image_url,
      notes: plant.notes ?? '',
      category: plant.category ?? CATEGORIES[0],
    });
    setSaveError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.german_name.trim() || !form.botanical_name.trim()) {
      setSaveError('Deutscher Name und botanischer Name sind Pflichtfelder.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId) {
        const updated = await api.updatePlant(editingId, form);
        setPlants((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      } else {
        const created = await api.createPlant(form);
        setPlants((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deletePlant(id);
      setPlants((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert('Löschen fehlgeschlagen: ' + (err instanceof Error ? err.message : err));
    }
  };

  const handleOcrSuggestion = (germanName: string, botanicalName: string) => {
    setForm((f) => ({
      ...f,
      german_name: germanName || f.german_name,
      botanical_name: botanicalName || f.botanical_name,
    }));
    setShowOcr(false);
    setShowForm(true);
  };

  // Filter tabItems to only show categories with at least 1 plant
  const tabItems = ['Alle', ...CATEGORIES.filter((cat) => counts[cat] > 0)];

  return (
    <>
      {/* Nav */}
      <nav className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 nav-glass">
        <div className="flex items-center gap-3">
          <Link to="/" className="btn-icon" aria-label="Zurück zum Lernen">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-sage/15 flex items-center justify-center border border-sage/20">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
              </svg>
            </div>
            <span className="plant-name text-cream font-semibold">Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-creamDim/50 text-xs font-mono hidden sm:block">
            {plants.length} Einträge
          </span>
          <button onClick={() => setShowCsvImport(true)} className="btn-ghost text-xs">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            CSV Import
          </button>
          <button onClick={openAdd} className="btn-primary text-xs">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Hinzufügen
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-5">

        {/* Search */}
        <div className="relative max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-creamDim/40" width="15" height="15"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="input pl-9"
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            id="admin-search"
          />
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap gap-2">
          {tabItems.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200
                ${activeCategory === cat
                  ? 'bg-sage text-white border-sage shadow-btn'
                  : 'bg-white text-creamDim border-border hover:border-sage/50 hover:text-sage'}`}
            >
              {cat}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono
                ${activeCategory === cat ? 'bg-white/20 text-white' : 'bg-border/50 text-creamDim/60'}`}>
                {counts[cat] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="panel skeleton" style={{ height: 220 }} />
            ))}
          </div>
        ) : error ? (
          <div className="panel p-8 text-center">
            <p className="text-red-500 text-sm">{error}</p>
            <button onClick={load} className="btn-ghost mt-4 mx-auto">Erneut laden</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="panel p-12 text-center flex flex-col items-center gap-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" className="text-border">
              <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
            </svg>
            <p className="plant-name text-xl text-cream">
              {search ? 'Keine Ergebnisse' : activeCategory !== 'Alle' ? `Keine Einträge in "${activeCategory}"` : 'Noch keine Einträge'}
            </p>
            {!search && (
              <button onClick={openAdd} className="btn-primary">Ersten Eintrag hinzufügen</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((plant) => (
              <PlantCard key={plant.id} plant={plant} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* Footer Disclaimer */}
        <div className="mt-8 text-center pb-8">
          <p className="text-creamDim/30 text-[10px] uppercase tracking-wider max-w-sm mx-auto">
            Hinweis: Diese App ist rein für den privaten Gebrauch zu Lernzwecken bestimmt. 
            Verwendete Bilder können urheberrechtlich geschützt sein.
          </p>
        </div>
      </main>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-cream/20 backdrop-blur-sm" onClick={closeForm} />
          <div className="relative panel w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 flex flex-col gap-5 animate-fadeIn">
            <div className="flex items-center justify-between">
              <h2 className="plant-name text-xl font-semibold text-cream">
                {editingId ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
              </h2>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowOcr(true)} className="btn-ghost text-xs" title="Namen aus Foto importieren">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  OCR
                </button>
                <button onClick={closeForm} className="btn-icon" aria-label="Schließen">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSave} className="flex flex-col gap-4">

              {/* Category */}
              <div>
                <label htmlFor="form-category" className="text-xs text-creamDim/70 mb-1 block">
                  Kategorie
                </label>
                <select
                  id="form-category"
                  className="input"
                  value={form.category ?? CATEGORIES[0]}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* German name */}
              <div>
                <label htmlFor="form-german" className="text-xs text-creamDim/70 mb-1 block">
                  Deutscher Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="form-german"
                  className="input plant-name"
                  placeholder="z.B. Spitzwegerich"
                  value={form.german_name}
                  onChange={(e) => setForm((f) => ({ ...f, german_name: e.target.value }))}
                  required
                />
              </div>

              {/* Botanical name */}
              <div>
                <label htmlFor="form-botanical" className="text-xs text-creamDim/70 mb-1 block">
                  Botanischer Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="form-botanical"
                  className="input botanical"
                  placeholder="z.B. Plantago lanceolata"
                  value={form.botanical_name}
                  onChange={(e) => setForm((f) => ({ ...f, botanical_name: e.target.value }))}
                  required
                />
                {form.botanical_name && (
                  <p className="botanical text-sage text-sm mt-1 pl-1">{form.botanical_name}</p>
                )}
              </div>

              {/* Image */}
              <div>
                <label className="text-xs text-creamDim/70 mb-1 block">Bild (optional)</label>
                <ImageUpload
                  value={form.image_url ?? null}
                  onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                />
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="form-notes" className="text-xs text-creamDim/70 mb-1 block">
                  Notizen (optional)
                </label>
                <textarea
                  id="form-notes"
                  className="textarea"
                  placeholder="Merkmale, Standort, Verwendung…"
                  value={form.notes ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                />
              </div>

              {saveError && (
                <p className="text-red-500 text-xs flex items-center gap-1 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {saveError}
                </p>
              )}

              <div className="flex gap-3 justify-end pt-1">
                <button type="button" onClick={closeForm} className="btn-ghost">Abbrechen</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Speichern…</>
                  ) : (
                    <>{editingId ? 'Speichern' : 'Hinzufügen'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OCR Modal */}
      {showOcr && (
        <OcrImport onSuggestion={handleOcrSuggestion} onClose={() => setShowOcr(false)} />
      )}

      {/* CSV Import Modal */}
      {showCsvImport && (
        <CsvImport 
          onClose={() => setShowCsvImport(false)} 
          onComplete={() => {
            setShowCsvImport(false);
            load();
          }} 
        />
      )}
    </>
  );
};
